import React, { useEffect, useState } from "react";
import { Table, Spin, Tag, Drawer } from "antd";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-daterangepicker/daterangepicker.css";

import SidebarNav from "../sidebar";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { sanitizeInput } from "../../../config/validation";
import { getLogsApi, getLogActionsApi } from "../../../service/api";
import { triggerDownload } from "../../../config/downloadUtils";

const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "40px",
    width: "100%",
    minWidth: "120px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    fontSize: "14px",
    padding: "0px 4px",
    backgroundColor: "#fff",
  }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  indicatorSeparator: () => ({ display: "none" }),
};

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "audit", label: "Audit" },
  { value: "api", label: "API" },
  { value: "error", label: "Error" },
  { value: "admin", label: "Admin" },
  { value: "event", label: "Event" },
];

const SERVICE_OPTIONS = [
  { value: "", label: "All backends" },
  { value: "main", label: "Main backend" },
  { value: "order", label: "Order backend" },
  { value: "wallet", label: "Wallet backend" },
];

const TYPE_COLORS = {
  audit: "blue",
  api: "default",
  error: "red",
  admin: "purple",
  event: "cyan",
};

const SERVICE_COLORS = {
  main: "geekblue",
  order: "gold",
  wallet: "magenta",
};

const ALL_COLUMNS = [
  {
    key: "timestamp",
    title: "Time",
    defaultVisible: true,
    render: (r) => (
      <span style={{ fontSize: 12, fontFamily: "monospace" }}>
        {r?.timestamp
          ? moment(r.timestamp).utcOffset("+05:30").format("YYYY-MM-DD hh:mm:ss.SSS A")
          : "-"}
      </span>
    ),
  },
  {
    key: "service",
    title: "Backend",
    defaultVisible: true,
    render: (r) => (
      <Tag color={SERVICE_COLORS[r?.service] || "default"}>
        {r?.service || "-"}
      </Tag>
    ),
  },
  {
    key: "type",
    title: "Type",
    defaultVisible: true,
    render: (r) => (
      <Tag color={TYPE_COLORS[r?.type] || "default"}>{r?.type || "-"}</Tag>
    ),
  },
  {
    key: "action",
    title: "Action",
    defaultVisible: true,
    render: (r) =>
      r?.action ? <code style={{ fontSize: 11 }}>{r.action}</code> : "-",
  },
  {
    key: "user",
    title: "User",
    defaultVisible: true,
    render: (r) => {
      const id = r?.userId || r?.adminUserId;
      const email = r?.userEmail || r?.adminEmail;
      return (
        <div style={{ fontSize: 12 }}>
          {email && <div>{email}</div>}
          {id && (
            <div style={{ color: "#888", fontFamily: "monospace" }}>{id}</div>
          )}
          {!id && !email && "-"}
        </div>
      );
    },
  },
  {
    key: "ms",
    title: "Method/Status",
    defaultVisible: true,
    render: (r) => {
      if (!r?.method && !r?.statusCode) return "-";
      return (
        <span style={{ fontSize: 12 }}>
          {r.method && <Tag>{r.method}</Tag>}
          {r.statusCode && (
            <Tag
              color={
                Number(r.statusCode) >= 500
                  ? "red"
                  : Number(r.statusCode) >= 400
                  ? "orange"
                  : "green"
              }
            >
              {r.statusCode}
            </Tag>
          )}
        </span>
      );
    },
  },
  {
    key: "path",
    title: "Path",
    defaultVisible: true,
    render: (r) => (
      <span style={{ fontSize: 12, fontFamily: "monospace" }}>
        {r?.path || "-"}
      </span>
    ),
  },
  {
    key: "ip",
    title: "IP",
    defaultVisible: false,
    render: (r) => <span style={{ fontSize: 12 }}>{r?.ip || "-"}</span>,
  },
  {
    key: "durationMs",
    title: "Duration",
    defaultVisible: false,
    render: (r) =>
      r?.durationMs ? <span style={{ fontSize: 12 }}>{r.durationMs}ms</span> : "-",
  },
  {
    key: "requestId",
    title: "Request ID",
    defaultVisible: false,
    render: (r) => (
      <span style={{ fontSize: 11, fontFamily: "monospace" }}>
        {r?.requestId || "-"}
      </span>
    ),
  },
];

const Logs = () => {
  const history = useHistory();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [actionOptions, setActionOptions] = useState([
    { value: "", label: "All actions" },
  ]);

  // filters
  const [userId, setUserId] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [requestId, setRequestId] = useState("");
  const [type, setType] = useState({ value: "", label: "All types" });
  const [action, setAction] = useState({ value: "", label: "All actions" });
  const [serviceFilter, setServiceFilter] = useState({
    value: "",
    label: "All backends",
  });
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(
    moment().subtract(24, "hours").startOf("hour"),
  );
  const [endDate, setEndDate] = useState(moment());

  // detail drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [meta, setMeta] = useState({ count: 0, scannedRecords: 0 });

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "logs-list",
    allColumns: ALL_COLUMNS,
    defaultPageSize: 25,
  });

  const fetchActions = async () => {
    try {
      const res = await getLogActionsApi();
      if (res?.data?.actions) {
        setActionOptions([
          { value: "", label: "All actions" },
          ...res.data.actions.map((a) => ({ value: a, label: a })),
        ]);
      }
    } catch (e) {
      // not fatal
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        from: startDate.toISOString(),
        to: endDate.toISOString(),
        limit: 500,
      };
      if (userId.trim()) params.userId = userId.trim();
      if (userEmail.trim()) params.userEmail = userEmail.trim();
      if (requestId.trim()) params.requestId = requestId.trim();
      if (type.value) params.type = type.value;
      if (action.value) params.action = action.value;
      if (serviceFilter.value) params.service = serviceFilter.value;
      if (search.trim()) params.search = search.trim();

      const res = await getLogsApi(params);
      if (res?.status) {
        setLogs(res.data?.logs || []);
        setMeta({
          count: res.data?.count || 0,
          scannedRecords: res.data?.scannedRecords || 0,
        });
        setPage(1);
      } else {
        toast.error(res?.message || "Failed to fetch logs", { toastId: 'logs-fetch-error' });
        setLogs([]);
      }
    } catch (e) {
      toast.error(e?.data?.message || "Failed to fetch logs", { toastId: 'logs-fetch-error' });
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActions();
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to page 1 when client-side page size changes.
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const clearFilters = () => {
    setUserId("");
    setUserEmail("");
    setRequestId("");
    setType({ value: "", label: "All types" });
    setAction({ value: "", label: "All actions" });
    setServiceFilter({ value: "", label: "All backends" });
    setSearch("");
    setStartDate(moment().subtract(24, "hours").startOf("hour"));
    setEndDate(moment());
  };

  const handleDownload = (format) => {
    if (!logs?.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
    const cols = ALL_COLUMNS?.filter((c) => visibleColumnKeys?.includes(c?.key));
    triggerDownload(format, logs, cols, "logs");
  };

  const detailColumn = {
    key: "__details",
    title: "Details",
    width: 80,
    render: (_, row) => (
      <a
        onClick={(e) => {
          e.preventDefault();
          setDetailRow(row);
          setDetailOpen(true);
        }}
        href="#"
      >
        View
      </a>
    ),
  };

  const columns = [...dynamicColumns, detailColumn];

  const pagedLogs = logs.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h3 className="page-title">Logs</h3>
                <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                  CloudWatch Logs Insights · last 7 days hot · S3 archive after
                </p>
              </div>
              <div className="col-md-4 text-right">
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Showing <b>{logs.length}</b> · Scanned{" "}
                  <b>{meta.scannedRecords}</b>
                </span>
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={logs.length}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                  />
                  <Spin spinning={loading}>
                    <Table
                      columns={columns}
                      dataSource={pagedLogs}
                      rowKey={(r, i) =>
                        `${r.timestamp || ""}-${r.requestId || ""}-${i}`
                      }
                      pagination={false}
                      size="small"
                      scroll={{ x: 1200 }}
                    />
                    {logs.length > pageSize && (
                      <div className="d-flex justify-content-end mt-3">
                        <CustomPagination
                          key={`pg-${pageSize}`}
                          totalTabs={logs.length}
                          tabsPerPage={pageSize}
                          handleChange={setPage}
                        />
                      </div>
                    )}
                  </Spin>
                </div>
              </div>
            </div>

            <div className="list-layout__filters">
              <FilterPanel count={logs.length} onClear={clearFilters}>
                <FilterPanel.Field label="User Email">
                  <input
                    type="text"
                    placeholder="user@example.com"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="User ID">
                  <input
                    type="text"
                    placeholder="65a3f8e9c1d4b2a8e7f3c4d1"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Backend">
                  <Select
                    options={SERVICE_OPTIONS}
                    value={serviceFilter}
                    onChange={setServiceFilter}
                    styles={customStyles}
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Type">
                  <Select
                    options={TYPE_OPTIONS}
                    value={type}
                    onChange={setType}
                    styles={customStyles}
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Action">
                  <Select
                    options={actionOptions}
                    value={action}
                    onChange={setAction}
                    styles={customStyles}
                    isSearchable
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Request ID">
                  <input
                    type="text"
                    placeholder="req_a8f9c2"
                    value={requestId}
                    onChange={(e) => setRequestId(e.target.value)}
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Free text search">
                  <input
                    type="text"
                    placeholder="search inside log message"
                    value={search}
                    onChange={(e) => setSearch(sanitizeInput(e.target.value))}
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Date range">
                  <DateRangePicker
                    initialSettings={{
                      startDate: startDate.toDate(),
                      endDate: endDate.toDate(),
                      ranges: {
                        "Last 1 hour": [
                          moment().subtract(1, "hour").toDate(),
                          moment().toDate(),
                        ],
                        "Last 24 hours": [
                          moment().subtract(24, "hours").toDate(),
                          moment().toDate(),
                        ],
                        "Last 3 days": [
                          moment().subtract(3, "days").toDate(),
                          moment().toDate(),
                        ],
                        "Last 7 days": [
                          moment().subtract(7, "days").toDate(),
                          moment().toDate(),
                        ],
                      },
                      timePicker: true,
                      locale: { format: "YYYY-MM-DD HH:mm" },
                    }}
                    onApply={(_, picker) => {
                      setStartDate(picker.startDate);
                      setEndDate(picker.endDate);
                    }}
                  >
                    <input type="text" placeholder="Select date range" readOnly />
                  </DateRangePicker>
                </FilterPanel.Field>
                <button
                  type="button"
                  className="btn btn-primary w-100 mt-2"
                  onClick={fetchLogs}
                  disabled={loading}
                >
                  {loading ? "Searching..." : "Apply"}
                </button>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>

      <Drawer
        title="Log details"
        placement="right"
        width={560}
        onClose={() => setDetailOpen(false)}
        open={detailOpen}
      >
        {detailRow && (
          <pre
            style={{
              fontSize: 12,
              background: "#f5f5f5",
              padding: 12,
              borderRadius: 4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {JSON.stringify(detailRow.raw || detailRow, null, 2)}
          </pre>
        )}
      </Drawer>
    </>
  );
};

export default Logs;
